/**
 * The ONE code path for reading/writing E2EE payloads — used by BOTH the MCP server and the web client, so
 * there's no `encrypt(key, JSON.stringify(...))` / `JSON.parse(decrypt(...))` re-implemented per caller (and
 * therefore no drift). A `Codec` binds a derived key + a platform `Crypto` impl; `seal`/`open` do the
 * JSON+AES round-trip, `sealRaw`/`openRaw` handle the non-JSON payloads (image base64, WebRTC SDP/ICE).
 *
 * Async because the browser's Web Crypto is async-only; the node impl wraps synchronous `node:crypto` in
 * async to satisfy the SAME interface — interface + separate implementations for the node/browser divide,
 * with one shared codec + types on top.
 */

/** Platform crypto contract. `K` is the key handle type (Buffer on Node, CryptoKey in the browser). */
export interface Crypto<K> {
  deriveKey(pairingSecret: string): Promise<K>;
  encrypt(key: K, plaintext: string): Promise<string>;
  decrypt(key: K, ciphertext: string): Promise<string | null>;
}

export class Codec<K> {
  private constructor(
    private readonly crypto: Crypto<K>,
    private readonly key: K,
  ) {}

  /** Derive the content key from the pairing secret and bind a codec to it. */
  static async create<K>(crypto: Crypto<K>, pairingSecret: string): Promise<Codec<K>> {
    return new Codec(crypto, await crypto.deriveKey(pairingSecret));
  }

  /** Encrypt a JSON-serializable value → ciphertext. */
  seal(value: unknown): Promise<string> {
    return this.crypto.encrypt(this.key, JSON.stringify(value));
  }

  /** Decrypt + JSON-parse; null on ANY failure (wrong key, tamper, malformed JSON). */
  async open<T>(ciphertext: string): Promise<T | null> {
    const pt = await this.crypto.decrypt(this.key, ciphertext);
    if (pt === null) return null;
    try {
      return JSON.parse(pt) as T;
    } catch {
      return null;
    }
  }

  /** Encrypt a raw string (non-JSON payloads: image base64, WebRTC SDP/ICE). */
  sealRaw(plaintext: string): Promise<string> {
    return this.crypto.encrypt(this.key, plaintext);
  }

  /** Decrypt a raw string; null on failure. */
  openRaw(ciphertext: string): Promise<string | null> {
    return this.crypto.decrypt(this.key, ciphertext);
  }
}
